import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { Toaster } from 'react-hot-toast';
import { HamburgerMenu } from '@/components';
import { useTableBookingTicket } from '@/features/booking/hooks/useTableBookingTicket';
import { useLoadingContext, useAuth } from '@/hooks';

import {
  HeroSection,
  EventDetailsSection,
  BookingCategoryHero,
  FloatingPriceSummary,
  TimeSlotTabs,
  FloorPlanCarousel,
} from '@/features/booking/components';
import { useFloorPlans } from '@/features/booking/hooks/useFloorPlans';
import type { FloorPlanCarouselRef } from '@/features/booking/components/FloorPlanCarousel';
import type { FloorPlanResponse } from '@/features/booking/types/floorPlanTypes';
import { TableBookingSkeleton } from '@/components/skeleton';
import { DesktopAllowedAreaSection } from './DesktopAllowedAreaSection';
import { PerkCarousel } from '@/features/booking/components/PerkCarousel';
import DesktopTableSelectionModal from './DesktopTableSelectionModal';
import DesktopTableListView from '@/features/booking/components/desktop/DesktopTableListView';
import { MenuSection } from './MenuSection';

const TableBookingDesktop: React.FC = () => {
  const { eventBookingCategoryId } = useParams<{
    eventBookingCategoryId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isGuest, isAuthenticated } = useAuth();

  // Extract cartType and cartItemId from query parameters
  const cartTypeParam = searchParams.get('cartType');
  const initialCartTab: 'main' | 'guest' | null = 
    cartTypeParam === 'guest' || cartTypeParam === 'main' ? cartTypeParam : null;
  const cartItemIdParam = searchParams.get('cartItemId');

  const {
    eventSchedule,
    eventBookingCategory,
    selections,
    selectedTable,
    selectedTimeSlot,
    loading,
    error,
    syncingCart,
    priceBreakdown,
    availableTables,
    currentSelection,
    adjustedAddOnSchedules,
    selectTable,
    selectTimeSlot,
    setGuestCountForSlot,
    handleAddOnToggleForSlot,
    setAddOnQuantityForSlot,
    syncCartItems,
    removeAllItems,
    incompleteCarts,
    selectedCartTab,
    currentCart,
    isLoadingCarts,
    hasCartItemForEBC,
    hasCartChanges,
    hasSelections,
    switchCartTab,
    tableSize,
    timeSlots,
    soldOutCartTables,
    getItemCountForTab,
  } = useTableBookingTicket(eventBookingCategoryId || '', initialCartTab, cartItemIdParam);

  // Get club handle
  const clubHandle = eventSchedule?.event?.business?.handle || 
                     eventBookingCategory?.booking_category?.business?.handle || null;
  const pageCurrency = eventBookingCategory?.currency;
  const timezone = eventSchedule?.timezone_id;
  
  const rawItemType = eventBookingCategory?.booking_category?.item_type || 'TABLE';
  const displayItemType = rawItemType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());

  // Log warning if currency is missing from API
  if (!pageCurrency && eventBookingCategory) {
    console.warn('TableBookingDesktop: currency missing from eventBookingCategory');
  }

  // Floor plan
  const bookingCategoryId = eventBookingCategory?.booking_category?.id;
  const { floorPlans, loading: floorPlansLoading } = useFloorPlans(bookingCategoryId);
  const floorPlanCarouselRef = useRef<FloorPlanCarouselRef>(null);
  const [currentFloorPlan, setCurrentFloorPlan] = useState<FloorPlanResponse | null>(null);

  const handleFloorPlanChange = useCallback((fp: FloorPlanResponse) => {
    setCurrentFloorPlan(fp);
  }, []);


  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Compute timeslots that have user selections (count of tables)
  const slotsWithCounts = useMemo(() => {
    const counts = new Map<string, number>();
    selections.forEach(tableMap => {
      tableMap.forEach((sel, slotKey) => {
        if (sel.guestCount > 0) {
          counts.set(slotKey, (counts.get(slotKey) || 0) + 1);
        }
      });
    });
    return counts;
  }, [selections]);

  // Trigger loading when data is loading
  useEffect(() => {
    if (loading) {
      startLoading('table-booking-ticket-data');
    } else {
      stopLoading('table-booking-ticket-data');
    }
    
    return () => {
      stopLoading('table-booking-ticket-data');
    };
  }, [loading, startLoading, stopLoading]);

  // Determine button text and action
  const getButtonText = (): string => {
    if (!hasSelections && !hasCartItemForEBC) {
      return 'Add to Cart';
    }
    if (hasSelections && hasCartItemForEBC && hasCartChanges) {
      return 'Update Cart';
    }
    if (!hasSelections && hasCartItemForEBC) {
      return 'Remove Items';
    }
    return 'Add to Cart';
  };

  const handleCartAction = async () => {
    // If no selections and cart has items, remove all items
    if (!hasSelections && hasCartItemForEBC) {
      await removeAllItems();
      // Cart count will auto-refresh via header's stale-while-revalidate
      return;
    }

    // If no selections and no cart, do nothing
    if (!hasSelections && !hasCartItemForEBC) {
      return;
    }

    // Sync cart items
    try {
      await syncCartItems();
      // Cart count will auto-refresh via header's stale-while-revalidate
    } catch (error) {
      console.error('Error syncing cart:', error);
      // Error handling is done in the hook
    }
  };

  const handleBack = () => {
    if (eventSchedule?.event?.business?.handle && eventSchedule?.event?.id) {
      navigate(`/venues/${eventSchedule.event.business.handle}/events/${eventSchedule.event.id}`);
    } else {
      navigate(-1);
    }
  };

  const handleAddTableClick = () => {
    if (selectedTable && selectedTimeSlot && tableSize) {
      if (currentSelection && currentSelection.guestCount > 0) {
        setGuestCountForSlot(selectedTable, selectedTimeSlot, 0);
      } else {
        setGuestCountForSlot(selectedTable, selectedTimeSlot, tableSize);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // When a table is selected, open the modal
  const handleTableSelect = (tableNumber: string) => {
    selectTable(tableNumber);
    setIsModalOpen(true);
  };

  const handleModalToggle = (addOnId: string, isSelected: boolean, guestIndex?: number) => {
    if (selectedTable && selectedTimeSlot) {
      handleAddOnToggleForSlot(selectedTable, selectedTimeSlot, addOnId, isSelected, guestIndex);
    }
  };

  const handleQuantityChange = (addOnId: string, guestIndex: number, quantity: number) => {
    if (selectedTable && selectedTimeSlot) {
      setAddOnQuantityForSlot(selectedTable, selectedTimeSlot, addOnId, guestIndex, quantity);
    }
  };

  // Get selected add-ons for current slot
  const getSelectedAddOns = (): Record<string, number> => {
    if (!currentSelection) return {};
    
    const selected: Record<string, number> = {};
    currentSelection.addOnIdsPerGuest.forEach(guestAddOns => {
      guestAddOns.forEach(addOnId => {
        selected[addOnId] = (selected[addOnId] || 0) + 1;
      });
    });
    
    return selected;
  };


  const allFloorPlanItems = useMemo(() => {
    return floorPlans.flatMap(fp => fp.items.map(item => ({ ...item, floorPlanName: fp.name })));
  }, [floorPlans]);

  // Sort floor plans by total available seats for the selected slot (only when no cart items)
  const sortedFloorPlans = useMemo(() => {
    if (hasCartItemForEBC || floorPlans.length <= 1) return floorPlans;

    const availableSet = new Set(availableTables);

    const scored = floorPlans.map(fp => {
      const totalSeats = fp.items.reduce((sum, item) => {
        if (availableSet.has(item.table_number)) {
          return sum + (item.table_size || 0);
        }
        return sum;
      }, 0);
      return { fp, totalSeats };
    });

    scored.sort((a, b) => b.totalSeats - a.totalSeats);
    return scored.map(s => s.fp);
  }, [floorPlans, availableTables, hasCartItemForEBC]);

  // When a table is selected from the grid, navigate the carousel to the floor plan containing that table
  useEffect(() => {
    if (!selectedTable || sortedFloorPlans.length === 0) return;

    const targetIndex = sortedFloorPlans.findIndex((fp) =>
      fp.items.some((item) => item.table_number === selectedTable)
    );
    if (targetIndex >= 0) {
      floorPlanCarouselRef.current?.goToIndex(targetIndex);
    }
  }, [selectedTable, sortedFloorPlans]);

  // Compute local vs in-cart tables for the currently selected timeslot
  const { inCartTables, localTables } = useMemo(() => {
    const inCart: string[] = [];
    const local: string[] = [];
    if (!selectedTimeSlot) return { inCartTables: inCart, localTables: local };

    selections.forEach((tableMap, tableNumber) => {
      const selection = tableMap.get(selectedTimeSlot);
      if (selection && selection.guestCount > 0) {
        if (selection.cartItemId) {
          inCart.push(tableNumber);
        } else {
          local.push(tableNumber);
        }
      }
    });

    return { inCartTables: inCart, localTables: local };
  }, [selections, selectedTimeSlot]);

  if (loading) {
    return <TableBookingSkeleton />;
  }

  if (error || !eventSchedule || !eventBookingCategory) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Booking Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The booking option you are looking for does not exist.'}
          </p>
          <button 
            onClick={handleBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const event = eventSchedule?.event;
  const business = eventBookingCategory?.booking_category?.business;
  const selectedAddOns = getSelectedAddOns();

  return (
    <>
    <div className="bg-black min-h-screen text-white font-sans overflow-x-clip">
      <HamburgerMenu clubHandle={clubHandle} isAuthenticated={isAuthenticated} />
      <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: { 
            background: '#1f2937', 
            color: ' #fff',
            border: '1px solid #374151'
          },
          duration: 4000,
        }} 
      />
      
      {/* Page Header (100vh on desktop) */}
      <div className="flex flex-col w-full relative md:min-h-[100dvh] md:min-h-[600px] 2xl:min-h-[700px] overflow-hidden bg-black">
        {/* Hero Section */}
        <div className="relative w-full md:flex-1 flex flex-col min-h-[300px] md:min-h-[20vh] lg:min-h-[25vh] md:max-h-[50vh] 2xl:max-h-[40vh]">
          {/* Default Hero size on mobile, absolute stretch on desktop */}
          <div className="md:hidden">
            <HeroSection event={event} business={business} />
          </div>
          <div className="hidden md:block absolute inset-0">
            <HeroSection event={event} business={business} className="w-full h-full" />
          </div>
          
          {/* Floating Back Button */}
          <button
            onClick={handleBack}
            className="absolute top-6 left-6 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-200 group z-10"
          >
            <svg 
              className="w-5 h-5 transform group-hover:-translate-x-0.5 transition-transform duration-200" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {/* Event Details */}
        <div className="shrink-0 relative z-10 w-full">
          <EventDetailsSection 
            eventSchedule={eventSchedule} 
            eventBookingCategory={eventBookingCategory}
          />
        </div>

        {/* Booking Category Hero */}
        <div className="shrink-0 relative z-10 w-full flex md:flex-none">
          <BookingCategoryHero 
            eventBookingCategory={eventBookingCategory as any} 
            currency={pageCurrency} 
            isTableBooking={true}
          />
        </div>
      </div>

      {/* Cart Loading State */}
      {isLoadingCarts && (
        <div className="w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] pt-4">
          <div className="flex items-center justify-center gap-3 py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
            <span className="text-gray-400 text-sm font-medium">Checking your cart...</span>
          </div>
        </div>
      )}

      {/* Cart Tabs - Conditional rendering */}
      {!isLoadingCarts && incompleteCarts && incompleteCarts.length > 0 && (() => {
        const mainCart = incompleteCarts.find(c => !c.is_guest_cart);
        const guestCart = incompleteCarts.find(c => c.is_guest_cart);
        const mainCartHasItems = mainCart?.items && mainCart.items.length > 0;
        const guestCartHasItems = guestCart?.items && guestCart.items.length > 0;
        
        if (!mainCartHasItems && !guestCartHasItems) {
          return null;
        }
        
        const showTabs = !!guestCart && guestCartHasItems && (mainCartHasItems || !isGuest);
        
        if (!showTabs) {
          return null;
        }
        
        // Show Main Cart tab if: main cart has items OR (guest cart exists AND user is not a guest)
        const showMainCartTab = mainCartHasItems || (guestCart && !isGuest);
        
        return (
          <div className="w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] pt-6">
            <div className="flex gap-2 border-b border-gray-700">
              {showMainCartTab && (
                <button
                  onClick={() => switchCartTab('main')}
                  className={`px-6 py-3 font-medium transition-colors relative ${
                    selectedCartTab === 'main'
                      ? 'text-white border-b-2 border-blue-500'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  Main Cart{getItemCountForTab('main') > 0 ? ` (${getItemCountForTab('main')})` : ''}
                </button>
              )}
              
              <button
                onClick={() => switchCartTab('guest')}
                className={`px-6 py-3 font-medium transition-colors relative ${
                  selectedCartTab === 'guest'
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Guest Cart{getItemCountForTab('guest') > 0 ? ` (${getItemCountForTab('guest')})` : ''}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main Content */}
      <main className="py-12 pb-48">
        <div className="w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw]">
          
          <div className="mb-8">
            {/* Allowed Areas Section */}
            <DesktopAllowedAreaSection allowedAreas={eventBookingCategory?.booking_category?.allowed_areas || null} />

            {/* Perks Carousel */}
            <PerkCarousel perks={eventBookingCategory?.booking_category?.perks || []} />

            {/* Global Time Slot Selection (Hidden if no custom slot duration is defined or if only 1 slot is generated) */}
            {timeSlots.length > 1 && eventBookingCategory?.custom_slot_duration && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-8"
              >
                <TimeSlotTabs
                  timeSlots={timeSlots}
                  selectedTimeSlot={selectedTimeSlot}
                  onTimeSlotSelect={selectTimeSlot}
                  businessTimeZone={timezone}
                  slotsWithCounts={slotsWithCounts}
                />
              </motion.div>
            )}
          </div>

          {/* Conditionally show Floor Plan and Table List only after a timeslot is selected */}
          {selectedTimeSlot && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-6 lg:gap-8 xl:gap-10 items-stretch">
              {/* Left Column: Floor Plan — bleeds wider */}
            <div className="space-y-8 -mx-2 lg:-ml-4 lg:mr-0 flex flex-col min-w-0">
              {floorPlansLoading && (
                <div className="max-w-3xl mx-auto space-y-3 px-6">
                  <div className="h-6 w-32 bg-gray-800 rounded animate-pulse" />
                  <div className="w-full rounded-xl bg-gray-800 animate-pulse" style={{ aspectRatio: '3/2' }} />
                </div>
              )}
              {!floorPlansLoading && floorPlans.length > 0 && (
                <motion.div
                  className="w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <FloorPlanCarousel
                    ref={floorPlanCarouselRef}
                    floorPlans={sortedFloorPlans}
                    availableTables={availableTables}
                    selectedTable={selectedTable}
                    onTableSelect={handleTableSelect}
                    onFloorPlanChange={handleFloorPlanChange}
                    pricePerGuest={eventBookingCategory?.price || 0}
                    currency={eventBookingCategory?.currency}
                    inCartTables={inCartTables}
                    localTables={localTables}
                    soldOutCartTables={soldOutCartTables}
                  />
                </motion.div>
              )}
            </div>

            {/* Right Column: Table Groups (scrollable internal list, bounded by Left Column) */}
            <div className="relative w-full h-full min-h-[500px] md:min-h-0">
              <div className="md:absolute md:inset-0 flex flex-col px-4 lg:px-0 lg:pr-2 pb-8 min-w-0">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  className="flex flex-col flex-1 min-h-0 h-full"
                >
                  <DesktopTableListView
                    floorPlanItems={allFloorPlanItems}
                    availableTables={availableTables}
                    selectedTable={selectedTable}
                    onTableSelect={handleTableSelect}
                    inCartTables={inCartTables}
                    localTables={localTables}
                    soldOutCartTables={soldOutCartTables}
                    itemType={displayItemType}
                    pricePerGuest={eventBookingCategory?.price || 0}
                    currency={eventBookingCategory?.currency}
                  />
                </motion.div>
              </div>
            </div>
          </div>
          )}

          {/* Menu Section */}
          {eventBookingCategoryId && (
            <div className="mt-12">
              <MenuSection 
                eventBookingCategoryId={eventBookingCategoryId} 
                currency={pageCurrency || 'GBP'} 
              />
            </div>
          )}

        </div>
      </main>




      {/* Floating Price Summary - Always visible */}
      <FloatingPriceSummary
        priceBreakdown={{
          totalPrice: priceBreakdown.totalPrice,
          perGuestBreakdown: priceBreakdown.perSlot.flatMap(slot => 
            slot.perGuestBreakdown.map(guest => ({
              addOns: guest.addOns.map(addOn => ({
                addOn: {
                  add_on: {
                    name: addOn.addOn.add_on.name,
                  },
                },
                price: addOn.price,
              })),
              }))
            ),
          }}
          guestCount={priceBreakdown.perSlot.reduce((sum, slot) => sum + slot.guestCount, 0)}
          eventBookingCategory={eventBookingCategory}
          onAddToCart={handleCartAction}
          addingToCart={syncingCart}
          buttonText={getButtonText()}
          isUpdateMode={!!(currentCart && hasCartItemForEBC)}
          onUpdateCart={currentCart && hasCartItemForEBC ? handleCartAction : undefined}
          cartId={currentCart?.id || null}
          addOnCount={Object.values(selectedAddOns).reduce((sum, count) => sum + count, 0)}
          isLoadingCarts={isLoadingCarts}
          hasCartChanges={hasCartChanges}
          hasViewCartButton={!!(currentCart && currentCart.items && currentCart.items.length > 0)}
          hasCartItemForEBC={hasCartItemForEBC}
          isTableBooking={true}
          tableSlots={priceBreakdown.perSlot.map(slot => ({
            tableNumber: slot.tableNumber,
            tableSize: slot.tableSize,
            guestCount: slot.guestCount,
            basePrice: slot.basePrice,
          }))}
          currency={pageCurrency}
          itemType={displayItemType}
        />

      {/* Floating View Cart Button */}
      {currentCart && currentCart.items && currentCart.items.length > 0 && !isModalOpen && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 25, delay: 0.15 }}
          onClick={() => navigate(`/carts/${currentCart.id}`)}
          className="fixed bottom-8 right-3 md:right-4 lg:right-6 xl:right-10 2xl:right-[6vw] z-50 w-80 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center justify-center space-x-2 transition-all duration-200 border border-blue-500/50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="font-semibold">View Cart</span>
          <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold">
            {currentCart.items.length}
          </span>
        </motion.button>
      )}

      {/* Desktop Table Selection Modal */}
      {selectedTimeSlot && currentSelection && (
        <DesktopTableSelectionModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          floorPlan={currentFloorPlan}
          selectedTable={selectedTable}
          floorPlanItems={allFloorPlanItems}
          pricePerGuest={eventBookingCategory?.price || 0}
          currency={pageCurrency}
          categoryImageId={eventBookingCategory?.booking_category?.base_image_id}
          selectedTimeSlot={selectedTimeSlot}
          businessTimeZone={timezone}
          tableSize={tableSize}
          itemType={displayItemType}
          onAddClick={handleAddTableClick}
          guestCount={currentSelection.guestCount}
          addOns={adjustedAddOnSchedules || eventBookingCategory?.add_on_schedules || []}
          selectedAddOns={selectedAddOns}
          onAddOnToggle={handleModalToggle}
          addOnIdsPerGuest={currentSelection.addOnIdsPerGuest}
          onQuantityChange={handleQuantityChange}
          eventStartTime={eventSchedule?.start_time}
          earlyEntryDuration={eventSchedule?.early_entry_duration || null}
          onCartAction={handleCartAction}
          isSyncingCart={syncingCart}
          cartButtonText={getButtonText()}
          hasCartChanges={hasCartChanges}
          hasCartItemForEBC={hasCartItemForEBC}
        />
      )}
    </div>
    </>
  );
};

export default TableBookingDesktop;
